import '@fontsource-variable/literata/opsz.css';
import './styles/app.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { notice } from './components/Toast';
import { ErrorBoundary } from './app/ErrorBoundary';
import { player } from './audio/player';
import { course } from './content';
import { initStore } from './state/store';

/** Loads every clip into memory when the browser is idle, so playback starts at once. */
function warmAudio() {
  const srcs = course.items.flatMap((i) => Object.values(i.audio).map((c) => c!.src));
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (idle) idle(() => player.warm(srcs), { timeout: 4000 });
  else setTimeout(() => player.warm(srcs), 1500);
}

async function boot() {
  await initStore();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>,
  );
  warmAudio();
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    const { registerSW } = await import('virtual:pwa-register');
    registerSW({
      immediate: true,
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        // A new version downloads in full (pictures and audio, for offline use) before it takes
        // over, and until then the old one stays on screen: say so, and reload into it once it is
        // in charge (a lesson in progress keeps its place).
        const watch = (worker: ServiceWorker | null) => {
          if (!worker || !navigator.serviceWorker.controller) return; // the first install replaces nothing
          notice('Updating to the latest version…');
          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') window.location.reload();
            if (worker.state === 'redundant') notice(null);
          });
        };
        watch(registration.installing);
        registration.addEventListener('updatefound', () => watch(registration.installing));
        // A tab left open for days still looks for a new version.
        setInterval(() => registration.update().catch(() => undefined), 60 * 60 * 1000);
      },
    });
  }
}

boot();
